import React, { FC, useState, useEffect, useContext } from "react";
import { Form, Select, Button, Table, Space, Input, Row, Col } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { store } from '@root/store';
import {
  statisticsOfHospital, exportStatisticsOfHospital,
  statisticsOfHospitalOnProvince, exportStatisticsOfHospitalOnProvince,
  statisticsOfHospitalOnCity, exportStatisticsOfHospitalOnCity
} from '@root/common/service';
import { YearSelect, MonthSelect, HospitalSelect } from '@root/components/selectBox';
import { StatisticsContext, TotalViewProps } from '../index';
import { ChinaProvincePorps } from '@root/actions';
const { Option } = Select;

interface HospitalProps {
  averageWatchTime: number,
  cityCode: string,
  cityName: string,
  courseCount: number,
  courseDuration: number,
  hospitalType: number,
  hospitalTypeName: string,
  index: string,
  instituteId: number,
  instituteName: string,
  provinceCode: string,
  provinceName: string,
  watchTime: number,
  watchUserCount: number
}

const Hospital: FC = () => {
  const [form] = Form.useForm();
  const context = useContext(StatisticsContext);

  const [provinceList, setProvinceList] = useState<ChinaProvincePorps[]>([]);
  const [cityList, setCityList] = useState<ChinaProvincePorps[]>([]);
  const [total, setTotal] = useState<TotalViewProps | undefined>();
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableList, setTableList] = useState<HospitalProps[]>([]);
  const [tableTotal, setTableTotal] = useState<number>(0);

  const columns: ColumnsType<HospitalProps> = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
    },
    {
      title: '省份',
      dataIndex: 'provinceName',
      key: 'provinceName',
    },
    {
      title: '所在市',
      dataIndex: 'cityName',
      key: 'cityName',
    },
    {
      title: '医院名称',
      dataIndex: 'instituteName',
      key: 'instituteName',
    },
    {
      title: '医院类型',
      dataIndex: 'hospitalTypeName',
      key: 'hospitalTypeName',
    },
    {
      title: '课程数量',
      dataIndex: 'courseCount',
      key: 'courseCount',
    },
    {
      title: '课程时长',
      dataIndex: 'courseDuration',
      key: 'courseDuration',
    },
    {
      title: '学习人数',
      dataIndex: 'watchUserCount',
      key: 'watchUserCount',
    },
    {
      title: '学习时长/h',
      dataIndex: 'watchTime',
      key: 'watchTime',
    },
    {
      title: '平均学习时长/h',
      dataIndex: 'averageWatchTime',
      key: 'averageWatchTime',
    }
  ];

  /**
   * 获取列表内容
   * @param page 页码
   */
  const getStatisticsOfHospital = (page = 1) => {
    setTableLoading(true);
    const params = form.getFieldsValue();

    let getlist: any;
    switch (context.baseUserCategory) {
      case '10':
        getlist = statisticsOfHospital;
        break;

      case '11':
        getlist = statisticsOfHospitalOnProvince;
        break;

      case '15':
        getlist = statisticsOfHospitalOnCity;
        break;
    }

    if (getlist) {
      getlist(
        { pageNum: page, pageSize: 10 },
        { ...params }
      ).then((res: any) => {
        const { data: { tableDataInfo, totalCourseHospitalView } } = res;
        setTableList(tableDataInfo.rows);
        setTableTotal(tableDataInfo.total);
        setTotal(totalCourseHospitalView)
      }).finally(() => {
        setTableLoading(false);
      });
    }
  }

  /**
   * 选择省份触发，同于设置所在市下拉内容
   * @param selected 省份数据
   */
  const onProvinceChange = (selected: string) => {
    const province: ChinaProvincePorps | undefined = provinceList.find((province: ChinaProvincePorps) => {
      return province.value === selected;
    })
    
    console.log('分割线---------------')
    console.log(province)
    if (province && province.children) {
      setCityList(province.children)
    }
  }

  // 导出
  const onExport = () => {
    let params = form.getFieldsValue();
    let uri = '?';
    if (context && context.getUrlParams) {
      uri += context.getUrlParams(params)
    }

    // 根据角色判断导出所使用的接口
    let exportfun: any
    switch (context.baseUserCategory) {
      case '10':
        exportfun = exportStatisticsOfHospital;
        break;

      case '11':
        exportfun = exportStatisticsOfHospitalOnProvince;
        break;

      case '15':
        exportfun = exportStatisticsOfHospitalOnCity;
        break;
    }

    if (exportfun) {
      exportfun(uri).then((res: any) => {
        if (context && context.excelDownload) {
          context.excelDownload(res);
        }
      })
    }
  }

  useEffect(() => {
    setProvinceList(store.getState().directionaries.chinaProvinceData);

    if (context && context.hospitalSelected) {
      form.setFieldsValue({
        provinceCode: context.hospitalSelected.provinceCode,
        cityCode: context.hospitalSelected.cityCode,
      })
    }
    
    getStatisticsOfHospital()
  }, [])

  useEffect(() => {
    if (context && context.hospitalSelected) {
      onProvinceChange(context.hospitalSelected.provinceCode);
    }
  }, [provinceList])

  return (
    <div className="coursestatistics-country">
      <div className="coursestatistics-form-title">
        <Form
          name="customized_form_controls"
          layout="inline"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          form={form}
          onFinish={() => { getStatisticsOfHospital() }}
        >
          <Row>
            <Col span={6}>
              <YearSelect
                label="年度"
                name="year"
              />
            </Col>
            <Col span={6}>
              <MonthSelect
                label="月份"
                name="month"
              />
            </Col>

            {/* 只有在国家权限下，才展示省份、所在市筛选条件 */}
            {
              context.baseUserCategory === '10' ?
                <React.Fragment>
                  <Col span={6}>
                    <Form.Item label="省份" name="provinceCode">
                      <Select onSelect={onProvinceChange}>
                        {
                          provinceList.map((province, index) => {
                            return <Option key={index} value={province.value}>{province.label}</Option>
                          })
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="所在市" name="cityCode">
                      <Select>
                        {
                          cityList.map((province, index) => {
                            return <Option key={index} value={province.value}>{province.label}</Option>
                          })
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                </React.Fragment>
                :
                <React.Fragment>
                  <Col span={6}>
                    <HospitalSelect
                      label="医院名称"
                      name="instituteId"
                    />
                  </Col>
                  <Col span={6}>
                    <Form.Item name="hospitalType" label="医院类型">
                      <Select>
                        <Option value={2}>支援医院</Option>
                        <Option value={1}>受援医院</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </React.Fragment>
            }
          </Row>
          <Row>
            {
              context.baseUserCategory === '10' &&
              <React.Fragment>
                  <Col span={6}>
                    <HospitalSelect
                      label="医院名称"
                      name="instituteId"
                    />
                  </Col>
                  <Col span={6}>
                    <Form.Item name="hospitalType" label="医院类型">
                      <Select>
                        <Option value={2}>支援医院</Option>
                        <Option value={1}>受援医院</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </React.Fragment>
            }
            <Col
              span={context.baseUserCategory === '10' ? 12 : 24}
              style={{ textAlign: 'right' }}
            >
              <Space style={{ marginRight: 15 }}>
                <Button type="primary" htmlType="submit">
                  提交
                </Button>
                <Button onClick={() => {
                  form.resetFields();
                  getStatisticsOfHospital()
                }}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="coursestatistics-export">
        <Button type="primary" onClick={onExport}>导出</Button>
      </div>
      <div className="coursestatistics-title">
        {total &&
          `合计: 课程数量：${total.courseCount}；课程时长/h：${total.courseDuration}；学习人数：${total.watchUserCount}；学习时长/h：${total.watchTime}；平均学习时长/h：${total.averageWatchTime}`
        }
      </div>
      <div className="coursestatistics-table">
        <Table
          rowKey="index"
          columns={columns}
          dataSource={tableList}
          loading={tableLoading}
          pagination={{
            total: tableTotal,
            onChange: (page) => { getStatisticsOfHospital(page) },
            showTotal: (total) => {
              return '共' + (total || 0) + '条记录';
            },
          }}
        />
      </div>
    </div>
  )
}

export default Hospital